import {Component, effect, inject, model, ModelSignal, Signal} from '@angular/core';
import {
  DialogService,
  ITableAction,
  ReasoningDialogComponent,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
} from 'frontier/browserkit';
import {changes, EControlActions, EsvgFiles, INITIAL_STATE_TOKEN} from 'frontier/nucleus';
import {concatMap, filter, finalize, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ProcessOverviewStore} from '../../../service/process-overview.store';
import {CartItemsListService} from 'src/app/api/generated';
import AppControlGuids from '../../../../../../api/ControlGuids';
import {ICart, IProcessState} from "../../../service/state.interface";
import {ECalculusStateFilter} from "../../../../../config/state-filter.enum";

@Component({
  selector: 'app-cart-items-list',
  templateUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrls: [
    '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss'
  ],
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: CartItemsListComponent},
  ],
})
export class CartItemsListComponent extends TableControlComponent {
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _cartItemsListService: CartItemsListService = inject(CartItemsListService);
  private _dialogService: DialogService = inject(DialogService);

  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;

  override GUID: string = AppControlGuids.TCartItemsList;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Teilstorno',
      controlAction: EControlActions.partlyCancel,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn',
      icon: EsvgFiles.close,
      action: (): Observable<boolean> => this.partlyCancel(),
      isHidden: (): boolean => {
        return !(this.selectedProcess().category === ECalculusStateFilter.monitorPayment
          || this.selectedProcess()?.category === ECalculusStateFilter.clientAccessMessages);
      },
    },
    {
      displayName: 'Position kürzen',
      controlAction: EControlActions.cancelItemValue,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn',
      icon: EsvgFiles.calculate,
      action: (): Observable<any> => this.cancelItemValue(),
      isHidden: (): boolean => {
        return !(this.selectedProcess().category === ECalculusStateFilter.monitorPayment
          || this.selectedProcess()?.category === ECalculusStateFilter.clientAccessMessages);
      },
    },
    {
      displayName: 'Eigenanteil-Rechnung',
      controlAction: EControlActions.invoice,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'warn',
      icon: EsvgFiles.calculate,
      action: (): Observable<any> => this.updateCopayValue(),
      isHidden: (): boolean => {
        return !(this.selectedProcess().category === ECalculusStateFilter.monitorPayment
          || this.selectedProcess()?.category === ECalculusStateFilter.clientAccessMessages);
      },
    },
  ]);

  override gridOptions = {
    ...SharedGridOptions
  };

  constructor() {
    super();
    effect(() => {
      const selectedCart = this.selectedCart();
      if (selectedCart) {
        this.patchFilter({
          parent: selectedCart.obj,
          typename: 'cart',
        });
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter(evt => {
          return new Map([
            [AppControlGuids.TInvoiceList, new Set([...changes, EControlActions.reroll])],
            [AppControlGuids.TCartItemsList, new Set([...changes, EControlActions.partlyCancel])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((evt) => {
          if (evt.changeType === EControlActions.changeLine || evt.changeType === EControlActions.create || evt.changeType === EControlActions.reroll) {
            if (evt.GUID === AppControlGuids.TInvoiceList) {
              this.changeAndFetchInstance();
            }
          } else {
            this.refreshInfiniteCache();
          }
        })).subscribe(),
    );
  }

  private partlyCancel(): Observable<boolean> {
    return this._cartItemsListService.cartItemsListCancleItem(
      {
        InstanceId: this.instanceId(),
        RowObjects: this.selectedRows().map((row) => {
          return row.apiRow.obj;
        }),
      }
    ).pipe(finalize(() => this.gridApi?.hideOverlay()));
  }

  private cancelItemValue(): Observable<any> {
    return this._dialogService.dialog.open(ReasoningDialogComponent, {
        data: {title: 'Kürzungsbetrag (€)', inputType: 'number'}
      }
    ).afterClosed().pipe(
      concatMap((res: string) => {
        if (!res) return of(null)
        res = res.replace(',', '.');
        return this._cartItemsListService.cartItemsListCancleItemValue(
          {
            InstanceId: this.instanceId(),
            RowObject: this.selectedRows()[0].apiRow.obj,
            Value: Number(res)
          }
        ).pipe(finalize(() => {
          this.changeAndFetchInstance();
        }))
      })
    );
  }

  /**
   * Eigenanteil Rechnung
   * @private
   */
  private updateCopayValue(): Observable<any> {
    return this._dialogService.dialog.open(ReasoningDialogComponent, {
        data: {title: 'Kürzungsbetrag (€)', inputType: 'number'}
      }
    ).afterClosed().pipe(
      concatMap((res: string) => {
        if (!res) return of(null)
        res = res.replace(',', '.');
        return this._cartItemsListService.cartItemsListUpdateCopayValue(
          {
            InstanceId: this.instanceId(),
            RowObject: this.selectedRows()[0].apiRow.obj,
            Value: Number(res)
          }
        ).pipe(finalize(() => {
          this.changeAndFetchInstance();
        }))
      })
    );
  }
}
